import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy } from "@angular/core";
import { FieldType, FieldTypeConfig, FormlyFieldConfig } from "@ngx-formly/core";
import { tap } from "rxjs";
import { TypologicUtil } from "src/app/helpers/typologic-util";
import { UrlService } from "src/app/miscellaneous/url.service";
import { ICategory } from "src/app/models/category";
import { DataService } from "src/app/services/data.service";
import { FormService } from "src/app/services/form.service";
import { VocabularyService } from "src/app/services/vocabulary-service.service";

export enum TypologicAction {
  SEARCH = "search",
  CREATE = "create",
  EDIT = "edit",
  DETAIL = "detail",
}

@Component({
  selector: "typologic",
  template: ``,
})
export class Typologic extends FieldType<FieldTypeConfig> implements AfterViewInit, OnDestroy {
  vocabularies: any[] = [];
  fields: FormlyFieldConfig[] = [];
  private util!: TypologicUtil;

  constructor(
    private vocabularyService: VocabularyService,
    private dataService: DataService,
    private urlService: UrlService,
    private _formService: FormService,
    private cdRef: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit() {
    this.util = new TypologicUtil(this.props);
    this.loadVocabularies();
    this.subscribeToModelData();
  }

  ngAfterViewInit(): void {
    if (this.util.getAction() === TypologicAction.EDIT) {
      setTimeout(() => this.patchValue(), 500);
    }
  }

  loadVocabularies(): void {
    let vocabulariesEndpoint = this.util.getVocabulariesEndpoint();

    if (this.util.isConcatenable()) {
      const id = this.getId();
      vocabulariesEndpoint = id ? (vocabulariesEndpoint + "/" + id) : "";
    }

    if (this.util.getResumeIdObbligo()) {

      const id = sessionStorage.getItem('idObbligo');

      if (id) {
        vocabulariesEndpoint = `${vocabulariesEndpoint.replace(/\/$/, '')}/${id}`;
      }

    }

    console.log(vocabulariesEndpoint);

    switch (this.util.getAction()) {
      case TypologicAction.SEARCH:
      case TypologicAction.CREATE: {
        this.load(vocabulariesEndpoint, () => this.createNgSelectList());
        break;
      }
      case TypologicAction.EDIT: {
        this.load(
          vocabulariesEndpoint,
          () => this.createNgSelectList(),
          () => {
            this.cdRef.detectChanges();
            this.patchValue();
          }
        );
        break;
      }
      case TypologicAction.DETAIL: {
        this.load(vocabulariesEndpoint, () => this.createInputList());
        break;
      }
      default: {
        console.error(`Azione non supportata: ${this.util.getAction()}`);
        this.load(vocabulariesEndpoint, () => this.createNgSelectList());
        break;
      }
    }
  }

  load(
    endpoint: string,
    buildFields: () => FormlyFieldConfig[],
    callback?: () => void,
  ): void {
    const excludedIds = this.util.getExcludedVocabularyIds();

    this.dataService.getData(endpoint).subscribe((data: any[]) => {
      this.vocabularies = data.filter(item => !excludedIds?.includes(item.id));
      this.vocabularyService.emit(this.vocabularies);
      this.fields = buildFields();

      const formToUpdate = this.field.parent;
      const oldFields = this._formService.fieldsValue;

      formToUpdate!.fieldGroup = [...(formToUpdate!.fieldGroup || []), ...this.fields];

      this._formService.setFields([...oldFields]);

      if (callback) callback();
    });
  }

  patchValue(): void {
    if (this.getId()) {
      this.dataService
        .getData(this.util.getAssociableCategoriesEndpoint() + "/" + this.getId())
        .pipe(
          tap((categories: ICategory[]) => {
            categories.forEach((category: ICategory) => {
              const field = this.fields.find(
                (field: FormlyFieldConfig) => field.id === (category as any)[this.util.getAttrAssociable()]
              );
              if (field) {
                field.formControl?.setValue((category as any)[this.util.getAttrCategoryId()]);
              }
            });
          })
        )
        .subscribe();
    }
  }

  createInputList(): FormlyFieldConfig[] {
    return this.vocabularies.map((vocabulary: any) =>
      this.createInputField(vocabulary)
    );
  }

  createInputField(vocabulary: any): FormlyFieldConfig {
    const ngInput = { ...this.util.getField() };
    const ngInputProps = ngInput.props;
    const categoriesEndpoint = `${this.util.getCategoriesEndpoint()}/${vocabulary[this.util.getAttrVocabularyId()]}`;

    this.dataService.getData(categoriesEndpoint).subscribe((categories: any[]) => {
      const category = categories.find(
        (category: any) => category[this.util.getAttrAssociable()] === vocabulary[this.util.getAttrVocabularyId()]
      );

      const fieldProps = {
        ...ngInputProps,
        label: `${vocabulary[this.util.getAttrLabel()]}:`,
      };

      ngInput.formControl!.setValue(`${category?.[this.util.getAttrLabel()] || "N/D"}`);
      ngInput.props = fieldProps;
    });

    return ngInput;
  }

  createNgSelectList(): FormlyFieldConfig[] {
    return this.vocabularies.map((vocabulary: any) =>
      this.createNgSelectField(vocabulary)
    );
  }

  createNgSelectField(vocabulary: any): FormlyFieldConfig {
    const ngSelect = { ...this.util.getField() };
    const ngSelectProps = ngSelect.props;

    const fieldProps = {
      ...ngSelectProps,
      endpoint: `${this.util.getCategoriesEndpoint()}/${vocabulary[this.util.getAttrCategoryId()]}`,
      label: vocabulary[this.util.getAttrLabel()],
    };

    ngSelect.id = vocabulary[this.util.getAttrVocabularyId()];
    ngSelect.key = `${vocabulary[this.util.getAttrProp()]}`;
    ngSelect.props = fieldProps;

    return ngSelect;
  }

  subscribeToModelData(): void {
    this.dataService.modelData$.subscribe(() => {
      if (this.model && this.util.getFlatMapping())
        this.updateModelWithSelectedValues();
    });
  }

  updateModelWithSelectedValues(): void {
    const modelProp = this.util.getPropName();
    if (!this.model[modelProp]) {
      this.model[modelProp] = {};
    }
    this.vocabularies.forEach((vocabulary: any) => {
      const selectedCategories = this.fields
        .filter((field: FormlyFieldConfig) => field.id === vocabulary[this.util.getAttrVocabularyId()])
        .map((field: FormlyFieldConfig) => field.formControl?.value)
        .filter((value) => value != null && value !== "");

      if (selectedCategories.length > 0) {
        const vocabularyId = vocabulary[this.util.getAttrVocabularyId()];
        const endpoint = this.util.getVocabulariesEndpoint();
        const lastSegment = endpoint.split('/').filter(Boolean).pop();
        const key = `${vocabularyId}.${lastSegment}`;
        this.model[modelProp][key] = selectedCategories;
      }
    });
  }


  getId(): string {
    return this.urlService.getUrlId() as string;
  }


  /**
   * @inheritdoc
   */
  ngOnDestroy() {
    this.vocabularyService.clear()
    this.fields.forEach(field => field.formControl?.reset());
    if (this.model)
      this.model[this.util.getPropName()] = {};
  }
}
